// Connection and retry constants
export const CONNECTION_TIMEOUT = 10000; // 10 seconds
export const MAX_CONNECTION_ATTEMPTS = 3;
export const RETRY_DELAY_BASE = 1000; // 1 second base delay
export const CONNECTION_CHECK_INTERVAL = 100; // 100ms

// Media constants - 16:9 aspect ratio
export const VIDEO_RESOLUTION = {
  width: 1920,
  height: 1080, // 1920/1080 = 16/9 aspect ratio (Full HD)
};

export const VIDEO_CONSTRAINTS = {
  facingMode: 'user' as const,
  width: { ideal: VIDEO_RESOLUTION.width, min: 640, max: 3840 },
  height: { ideal: VIDEO_RESOLUTION.height, min: 480, max: 2160 },
  aspectRatio: { ideal: 16/9 }, // Enforce 16:9 aspect ratio
  frameRate: { ideal: 30, max: 60 }, // Support up to 60fps
};

// Quality presets for different network conditions
export const VIDEO_QUALITY_PRESETS = {
  HIGH: {
    width: 3840,
    height: 2160,
    frameRate: 30,
    bitrate: 8000000, // 8 Mbps
  },
  MEDIUM: {
    width: 1920,
    height: 1080,
    frameRate: 30,
    bitrate: 4000000, // 4 Mbps
  },
  LOW: {
    width: 1280,
    height: 720,
    frameRate: 30,
    bitrate: 2000000, // 2 Mbps
  },
} as const;

export const AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
};

// UI constants - 16:9 aspect ratio containers
export const VIDEO_GRID_LAYOUT = {
  localVideoWidth: '60%', // Local video takes 60% of available width
  remoteVideoWidth: '48%', // Remote videos take 48% width each
  aspectRatio: '16/9', // Force 16:9 aspect ratio for all video containers
  maxHeight: '70vh', // Prevent videos from being too tall
  maxWidth: '800px', // Maximum width for the video container
};

export const SIDEBAR_WIDTH = 300;

// Live streaming constants
export const STREAM_START_DELAY = 3000; // 3 seconds
export const TRACK_ATTACH_DELAY = 100; // 100ms
export const VIDEO_TRACK_WAIT_DELAY = 2000; // 2 seconds


// Device selection constants
export const DEVICE_SELECTION = {
  REFRESH_INTERVAL: 5000, // 5 seconds
  PERMISSION_TIMEOUT: 10000, // 10 seconds
} as const;

// Error messages
export const ERROR_MESSAGES = {
  ROOM_ID_REQUIRED: 'Room ID is required',
  MEDIA_PERMISSIONS_DENIED: 'Camera and microphone permissions are required for this application. Please grant permissions in your browser settings.',
  MEDIA_DEVICES_NOT_AVAILABLE: 'Media devices are not available in this environment. Please use HTTPS or localhost.',
  CONNECTION_TIMEOUT: 'Timeout waiting for WebRTC connection to be established',
  NO_PUBLISH_PERMISSION: 'Participant does not have permission to publish tracks',
  NO_VIDEO_TRACK: 'No video track available for egress',
  NO_AUDIO_TRACK: 'No audio track available for egress',
  VIDEO_TRACK_DISABLED: 'Video track is not enabled',
  AUDIO_TRACK_DISABLED: 'Audio track is not enabled',
  ROOM_NOT_CONNECTED: 'Room not connected',
  NO_ACTIVE_STREAM: 'No active stream to stop',
  FAILED_TO_CONNECT: 'Failed to connect to room',
  FAILED_TO_START_STREAM: 'Failed to start live stream',
  FAILED_TO_STOP_STREAM: 'Failed to stop live stream',
  DEVICE_SWITCH_FAILED: 'Failed to switch device',
  NO_DEVICES_FOUND: 'No media devices found. Please check your camera and microphone permissions.',
} as const;

// Success messages
export const SUCCESS_MESSAGES = {
  CONNECTED: 'Connected to room successfully',
  STREAM_STARTED: 'Live stream started successfully',
  STREAM_STOPPED: 'Live stream stopped successfully',
} as const;

// Animation durations
export const ANIMATION_DURATION = {
  PULSE: '2s',
} as const;

// Breakpoints
export const BREAKPOINTS = {
  MOBILE: 768,
} as const;
